import { FC, ChangeEvent, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import './style.scss'
import CONFIG from '../../../config'
import { Text, Input, Button, FormLabelError, FormLabel, FormTitle, FormMessageError, FormMessageSuccess } from '../UI'
import { Modal, useModal } from '../../components/UI/Modal'
import { userService } from '../../services'
import { profileActions } from '../../store/actions'

type ProfilePasswordFormProps = {
  className?: string,
  forgotPasswordSw?: boolean
}
export interface IProfilePasswordForm {
  passwordOld: string,
  password: string,
  confirmPassword: string
}

const ProfilePasswordForm:FC<ProfilePasswordFormProps> = ({ className = '', forgotPasswordSw = false }) => {
  const dispatch = useDispatch()
  const defaultValues: IProfilePasswordForm = {
    passwordOld: '',
    password: '',
    confirmPassword: ''
  }
  const { register, handleSubmit, formState: { errors }, watch, setValue, setFocus, reset } = useForm<IProfilePasswordForm>({ defaultValues })
  const [submited, setSubmited] = useState<boolean>(false)
  const modal = useModal(false)
  const password = useRef({})
  password.current = watch('password', '')

  const fieldErrorRemove = (field: any, value: string) => {
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true
    })
  }

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    const fieldName = 'password'

    if(errors.password) {
      fieldErrorRemove(fieldName, value)
    } else {
      setValue(fieldName, value)
    }
    setTimeout(() => setFocus(fieldName), 0)
  }

  const postForm = async (passwordsData: any) => {
    const resp = await userService.updateUserCurrentPassword(passwordsData)

    if(resp && resp.status === 'Ok') {
      reset(defaultValues)
      dispatch(profileActions.changePasswordProfileSw(false))
      modal.setStatus('SUCCESS')
    } else {
      modal.setStatus('ERROR')
      modal.setMessage(resp && resp.message ? resp.message : '')
    }
    modal.open();
    setSubmited(false)
  }

  const onSubmit = async (passwordsData: IProfilePasswordForm) => {
    const {
      passwordOld,
      password
    } = passwordsData

    setSubmited(true)
    postForm({
      oldPassword: passwordOld,
      password: password,
      temporaryKeyStatus: forgotPasswordSw
    })
  }

  return (
    <>
      <div className={className}>
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <fieldset className="col-12 lg:col-5">
              <FormLabel htmlFor="passwordOld" required>Actual contraseña</FormLabel>
              {forgotPasswordSw ?
                  <Input
                    type="password"
                    id="passwordOld"
                    name="passwordOld"
                    tabIndex={1}
                    defaultValue="Ab123456"
                    disabled
                    fullWidth
                    className="mt-5"
                  />
                :
                  <Input
                    {...register('passwordOld',
                      {
                        required: 'Digita Actual contraseña',
                        pattern: {
                          value: CONFIG.FORM.PATTERN.PASSWORD.EXP,
                          message: 'Digita Contraseña válida'
                        },
                        minLength: {
                          value: CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT,
                          message: `Digita mínimo ${CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT} caracteres`
                        }
                      }
                    )}
                    type="password"
                    id="passwordOld"
                    name="passwordOld"
                    placeholder="Ingresa tu actual contraseña"
                    tabIndex={1}
                    fullWidth
                    autoComplete="old-password"
                    passwordShow
                    error={!!errors.passwordOld}
                    className="mt-5"
                  />
              }
              {errors.passwordOld && <FormLabelError>{errors.passwordOld?.message}</FormLabelError>}
            </fieldset>
          </div>

          <div className="row mt-30">
            <fieldset className="col-12 lg:col-5">
              <FormLabel htmlFor="password" required>Nueva contraseña</FormLabel>
              <Input
                {...register('password',
                  {
                    required: 'Digita Nueva contraseña',
                    pattern: {
                      value: CONFIG.FORM.PATTERN.PASSWORD.EXP,
                      message: 'Digita Contraseña válida'
                    },
                    minLength: {
                      value: CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT,
                      message: `Digita mínimo ${CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT} caracteres`
                    }
                  }
                )}
                type="password"
                id="password"
                name="password"
                placeholder="Ingresa una nueva contraseña"
                tabIndex={2}
                fullWidth
                autoComplete="new-password"
                passwordShow
                error={!!errors.password}
                className="mt-5"
                onChange={onPasswordChange}
              />
              {errors.password && <FormLabelError>{errors.password?.message}</FormLabelError>}
              <Text component="label" color="gray-400" size="11" className="lg:fz-12 mt-10 lg:ml-10">
                Mínimo:<br />
                - 8 caracteres<br />
                - 1 mayúscula<br />
                - 1 minúscula<br />
                - 1 número.
              </Text>
            </fieldset>
          </div>

          <div className="row mt-30">
            <fieldset className="col-12 lg:col-5">
              <FormLabel htmlFor="confirmPassword" required>Confirmar nueva contraseña</FormLabel>

              <Input
                {...register('confirmPassword',
                  {
                    required: 'Confirmar nueva contraseña',
                    pattern: { value: CONFIG.FORM.PATTERN.PASSWORD.EXP,
                      message: 'Digita Contraseña válida'
                    },
                    minLength: {
                      value: CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT,
                      message: `Digita mínimo ${CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT} caracteres`
                    },
                    validate: value => value === password.current || 'Las contraseñas no coinciden'
                  }
                )}
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Reingresa tu nueva contraseña"
                tabIndex={3}
                fullWidth
                passwordShow
                error={!!errors.confirmPassword}
                className="mt-5"
              />
              {errors.confirmPassword && <FormLabelError>{errors.confirmPassword?.message}</FormLabelError>}
            </fieldset>
          </div>

          <FormTitle className="mt-40" required>Campo obligatorio</FormTitle>

          <div className="lg:d-none mt-50">
            <Button type="submit" fullWidth disabled={submited}>GUARDAR CAMBIOS</Button>
          </div>

          <div className="d-none lg:d-block mt-40">
            <Button type="submit" size="md" disabled={submited}>GUARDAR CAMBIOS</Button>
          </div>
        </form>
      </div>

      <Modal show={modal.show} maxWidth="345px" onClose={() => modal.close()}>
        {modal.status === 'SUCCESS' &&
          <FormMessageSuccess
            title="¡Los cambios fueron guardados con éxito!"
            className="pt-80 pb-100 px-15"
          />
        }
        {modal.status === 'ERROR' &&
          <FormMessageError
            message={modal.message}
            className="pt-80 pb-70 px-15"
            onClick={() => modal.close()}
          />
        }
      </Modal>
    </>
  )
}

export { ProfilePasswordForm }